/**
 * HandLandmarkerとWebGLの組み合わせたアプリケーション
 */
import { HandLandmarkerApp, type ProcessLandmarkerData } from './landmarker/HandLandmarkerApp';
import { startWebcam } from './utils/startWebcam';
import { WebGlParticle } from './webGL/WebGlParticle';

export class App {
  private video: HTMLVideoElement;
  private handLandmarkerApp: HandLandmarkerApp;
  private webGlParticle: WebGlParticle;

  private lastFrameTime: number = 0;
  private frameInterval = 1000 / 60; // フレーム間隔
  private drawLandmarkerFlag: boolean = false; // Landmarker（骨格など）を描画する場合はtrue

  constructor(
    video: HTMLVideoElement,
    landmarkerCanvas: HTMLCanvasElement, // HandLandmarkerの描画用キャンバス
    webglCanvas: HTMLCanvasElement, // WebGLの描画用キャンバス
  ) {
    this.video = video;

    // Initialize
    this.handLandmarkerApp = new HandLandmarkerApp(video, landmarkerCanvas);
    this.webGlParticle = new WebGlParticle(webglCanvas);
  }

  async initialize(): Promise<void> {
    await this.handLandmarkerApp.initialize(); // HandLandmarkerを初期化
    await startWebcam(this.video); // webCamを起動
    // Animation開始
    this.animate();
  }

  private animate = (currentTime: number = 0): void => {
    // FPSを制限するための処理
    const deltaTime = currentTime - this.lastFrameTime;
    if (deltaTime >= this.frameInterval) {
      // Landmarkerの描写と座標系を変換しWebGLにわたす処理
      this.detectionHandler(currentTime);

      this.lastFrameTime = currentTime;
    }
    requestAnimationFrame(this.animate);
  }

  // Helper ------------------------
  /**
   * 検出データを取得し、Landmarkerの描写と座標系を変換しWebGLにわたす処理を行う
   * @param currentTime
   */
  private detectionHandler(currentTime: number) {
    // 取得した骨格の座標と利き手のデータ
    const detectionResult = this.handLandmarkerApp.detectLandmarker(currentTime);
    if (detectionResult) {
      // デバッグ用の描写を行う場合
      if (this.drawLandmarkerFlag) {
        this.handLandmarkerApp.drawLandmarker(detectionResult);
      }
      const processedDataArray = this.handLandmarkerApp.processLandmarkerData(detectionResult);
      this.webGlParticle.update(processedDataArray, currentTime * 0.001);
    } else {
      // 検出が失敗した場合、空のデータをセット
      const emptyData: ProcessLandmarkerData = {
        leftIndexFinger: null,
        rightIndexFinger: null,
      };
      this.webGlParticle.update(emptyData, currentTime * 0.001);
    }
  }
}
